import { ReactNode } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Card } from '@factorialco/f0-react'

import { Element } from 'modules/forms/lib/Element'
import { Answer } from 'modules/forms/lib/types'

type Props = {
  children?: ReactNode
  element: Element
  mode?: 'answer' | 'response' | 'preview' | 'edit'
  handleAnswer?: (answer: Answer, elementId: string) => void
  showElementName?: boolean
  titleSize?: 'default' | 'small'
}

export const ElementPreview = ({
  children,
  element,
  mode = 'preview',
  handleAnswer,
  showElementName = true,
  titleSize = 'default',
}: Props) => {
  const isMandatory = element?.mandatory ?? false

  const renderElementContent = () => {
    switch (mode) {
      case 'preview':
        return element.getPreviewComponent(false, true)
      case 'answer':
        if (handleAnswer)
          return element.getQuestionComponent((answer) => handleAnswer(answer, element.id))
      case 'response':
        return element.getPreviewComponent(true)
      case 'edit':
        return element.getPreviewComponent()
    }
  }

  const elementName = element.getName() + (isMandatory ? ' *' : '')
  const content = (
    <Box gap="s8" width="full" style={{ maxWidth: '720px' }}>
      <Box width="full">
        {titleSize === 'small' ? (
          <Text size="300" weight="bold">
            {element.label}
          </Text>
        ) : (
          <Text.Header2 weight="bold">{element.label}</Text.Header2>
        )}
      </Box>
      <Text size="200" color="grey800">
        {element.description}
      </Text>
      {renderElementContent()}
    </Box>
  )

  // Edit mode: use Card until we migrate the edit modal action outside the card.
  // Remove when we migrate the edit button.
  if (mode === 'edit') {
    return (
      <>
        {content}
        {children}
      </>
    )
  }

  if (mode === 'preview') {
    return (
      <F0Card description={showElementName ? elementName : ''}>
        <Box style={{ pointerEvents: 'auto' }}>
          {content}
          {children}
        </Box>
      </F0Card>
    )
  }

  if (mode === 'response') {
    return (
      <Box gap="s8" width="full" alignItems="flexStart" style={{ maxWidth: '720px' }}>
        {showElementName && (
          <Text size="200" weight="normal">
            {elementName}
          </Text>
        )}
        {content}
        {children}
      </Box>
    )
  }

  return (
    <Box gap="s8" width="full" alignItems="flexStart" style={{ maxWidth: '720px' }}>
      {showElementName && (
        <Text size="200" weight="normal">
          {elementName}
        </Text>
      )}
      {content}
      {children}
    </Box>
  )
}
